import { inject, Injectable } from '@angular/core';
import { Observable, catchError, from, throwError } from 'rxjs';
import {
  Brand,
  BrandList,
  BrandsApi,
  ListBrandsRequest,
  CreateBrand,
  PatchBrand,
  BrandLogoUploadIntent,
  BrandLogoUploadStatus,
  BrandFaviconUploadIntent,
  BrandFaviconUploadStatus,
} from '@tourfold/libraries/tf-api-client-public-v2';

@Injectable({
  providedIn: 'root',
})
export class BrandsService {
  private readonly brandsApi = inject(BrandsApi);

  getBrandsData(data: ListBrandsRequest): Observable<BrandList> {
    return from(
      this.brandsApi.listBrands({
        page: data.page ?? 0,
        size: data.size ?? 50,
        sort: data.sort,
        search: data.search,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  createBrand(createBrand: CreateBrand): Observable<Brand> {
    return from(this.brandsApi.createBrand({ createBrand })).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  patchBrand(brandId: string, patchBrand: PatchBrand): Observable<Brand> {
    return from(this.brandsApi.patchBrand({ brandId, patchBrand })).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  deleteBrand(brandId: string): Observable<void> {
    return from(this.brandsApi.deleteBrand({ brandId })).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  deleteBrandLogo(brandId: string): Observable<void> {
    return from(this.brandsApi.deleteBrandLogo({ brandId })).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  linkCustomDomain(brandId: string, customDomainId: string): Observable<void> {
    return from(this.brandsApi.linkCustomDomainToBrand({ brandId, customDomainId })).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  unlinkCustomDomain(brandId: string, customDomainId: string): Observable<void> {
    return from(this.brandsApi.unlinkCustomDomainFromBrand({ brandId, customDomainId })).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  createLogoUploadIntent(
    brandId: string,
    originalFileName: string,
    mimeType: string
  ): Observable<BrandLogoUploadIntent> {
    return from(
      this.brandsApi.createBrandLogoUploadIntent({
        brandId,
        createBrandLogoUploadIntent: {
          originalFileName,
          mimeType,
        },
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  uploadLogoToPresignedPost(
    uploadUrl: string,
    uploadFields: Record<string, string>,
    file: File
  ): Observable<void> {
    const formData = new FormData();
    Object.entries(uploadFields).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120_000);

    return from(
      fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Upload failed with status ${response.status}`);
          }
        })
        .finally(() => clearTimeout(timeoutId))
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getLogoUploadStatus(brandId: string, fileId: string): Observable<BrandLogoUploadStatus> {
    return from(this.brandsApi.getBrandLogoUploadStatus({ brandId, fileId })).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  deleteBrandFavicon(brandId: string): Observable<void> {
    return from(this.brandsApi.deleteBrandFavicon({ brandId })).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  createFaviconUploadIntent(
    brandId: string,
    originalFileName: string,
    mimeType: string
  ): Observable<BrandFaviconUploadIntent> {
    return from(
      this.brandsApi.createBrandFaviconUploadIntent({
        brandId,
        createBrandFaviconUploadIntent: {
          originalFileName,
          mimeType,
        },
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  uploadFaviconToPresignedPost(
    uploadUrl: string,
    uploadFields: Record<string, string>,
    file: File
  ): Observable<void> {
    const formData = new FormData();
    Object.entries(uploadFields).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120_000);

    return from(
      fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Upload failed with status ${response.status}`);
          }
        })
        .finally(() => clearTimeout(timeoutId))
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getFaviconUploadStatus(brandId: string, fileId: string): Observable<BrandFaviconUploadStatus> {
    return from(this.brandsApi.getBrandFaviconUploadStatus({ brandId, fileId })).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
